import React from 'react';
import { fromPairs, map, get, find, pick } from 'lodash';
import classNames from 'classnames';

// local dependencies
import { Form } from 'modules/form';
import { button as Button } from 'modules/button';
import { getLabel } from 'utils/getLabel';
import { checkbox as Checkbox } from 'ui/views/generic/checkbox';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { accessManagementUserGroupsCreateModeGroupTab as AccessManagementUserGroupsCreateModeGroupTab } from 'ui/views/widgets/accessManagementUserGroupsCreateModeGroupTab';
 *
 * // render
 * <AccessManagementUserGroupsCreateModeGroupTab propName='propValue'/>
 */
export const accessManagementUserGroupsCreateModeGroupTab = ( {
    selectedTabObj,
    formData,
    options,
    selectedPermissions,
    onCancelActionClick,
    onPermissionSelect,
    onFormDataChange,
    onGoToUsersActionClick
} ) => {
    return (
        <div className='ui-access-management-user-groups-create-mode-group-tab'>

            {/* Information Section */}
            <div className='ui-access-management-user-groups-create-mode-group-tab__information'>

                {/* Title */}
                <div className='ui-access-management-user-groups-create-mode-group-tab__information__title'>
                    { getLabel( selectedTabObj.informationSection.title ) }
                </div>

                {/* Form */}
                <Form
                    fields={ fromPairs( map( selectedTabObj.informationSection.fields, ( field ) => [ field.name, field ] ) ) } // convert to object as specified by `Form` prop
                    mode='form'
                    values={ formData }
                    onChangeCallBack={ onFormDataChange }
                    onSubmit={ () => onGoToUsersActionClick( selectedTabObj ) }
                >
                    { ( { fields, values, onChange, onSumbit } = {} ) => {

                        return (
                            <form
                                onSubmit={ onSumbit }
                                className='ui-access-management-user-groups-create-mode-group-tab__information__form'
                            >
                                { /* fields */ }
                                { map( fields, ( field ) => {

                                    const fieldClassName = classNames( 'ui-access-management-user-groups-create-mode-group-tab__information__form__field', `ui-access-management-user-groups-create-mode-group-tab__information__form__${field.name}` );

                                    return (
                                        <field.view
                                            className={ fieldClassName }
                                            key={ field.name }
                                            name={ field.name }
                                            placeholder={ field.placeholder }
                                            onChange={ onChange }
                                            value={ values[ field.name ] }
                                            showLabel={ false }
                                            config={ field.config }
                                            maxLength={ field.maxLength }
                                        />
                                    );
                                } ) }

                                {/* Permissions */}
                                <div className='ui-access-management-user-groups-create-mode-group-tab__information__form__permissions'>

                                    {/* Title */}
                                    <div className='ui-access-management-user-groups-create-mode-group-tab__information__form__permissions__title'>
                                        { getLabel( selectedTabObj.permissionsSection.title ) }
                                    </div>

                                    {/* Table */}
                                    <div className='ui-access-management-user-groups-create-mode-group-tab__information__form__permissions__container'>
                                        <div className='ui-access-management-user-groups-create-mode-group-tab__information__form__permissions__container__left-cell ui-access-management-user-groups-create-mode-group-tab__information__form__permissions__container__left-cell--first'></div>
                                        <div className='ui-access-management-user-groups-create-mode-group-tab__information__form__permissions__container__mid-cell ui-access-management-user-groups-create-mode-group-tab__information__form__permissions__container__mid-cell--first'></div>
                                        <div className='ui-access-management-user-groups-create-mode-group-tab__information__form__permissions__container__right-cell ui-access-management-user-groups-create-mode-group-tab__information__form__permissions__container__right-cell--first'>
                                            { values.userGroupName }
                                        </div>
                                        {
                                            map( options[ selectedTabObj.permissionsSection.options ], permissionOption => {
                                                return (
                                                    <React.Fragment key={ permissionOption.name } >
                                                        <div className='ui-access-management-user-groups-create-mode-group-tab__information__form__permissions__container__left-cell'>
                                                            { getLabel( permissionOption.title ) }
                                                        </div>
                                                        <div className='ui-access-management-user-groups-create-mode-group-tab__information__form__permissions__container__mid-cell'></div>
                                                        <div
                                                            className='ui-access-management-user-groups-create-mode-group-tab__information__form__permissions__container__right-cell'
                                                            onClick={ () => onPermissionSelect( pick( permissionOption, [ 'id', 'value' ] ) ) }
                                                        >
                                                            <Checkbox
                                                                name={ permissionOption.name }
                                                                value={ find( selectedPermissions, { id: permissionOption.id } ) !== undefined }
                                                            />
                                                        </div>
                                                    </React.Fragment>
                                                );
                                            } )
                                        }
                                    </div>
                                </div>
                                
                                {/* button actions */}
                                <div className='ui-access-management-user-groups-create-mode-group-tab__information__form__actions'>
                                    { map( selectedTabObj.actions, action => {
                                        return (
                                            <Button
                                                key={ action.name }
                                                id= { action.name }
                                                title={ action.title }
                                                size='large'
                                                type={ 'cancel' !== action.name ? 'submit' : 'button' }
                                                outline={ 'cancel' === action.name ? true : false }
                                                onClick={ 'cancel' === action.name ? onCancelActionClick : null }
                                                disabled={ 'cancel' !== action.name && ( ! get( values, 'userGroupName', '' ) || ! get( values, 'userGroupDescription', '' ) ) }
                                                showPostIcon={ 'goToUsers' === action.name }
                                                postIconId='forward'
                                                postIconMargin={ true }
                                                className='ui-access-management-user-groups-create-mode-group-tab__information__form__actions__action'
                                            />
                                        );
                                    } ) }
                                </div>
                            </form>
                        );
                    } }
                </Form>
            </div>
        </div>
    );
};

// set display name
accessManagementUserGroupsCreateModeGroupTab.displayName = 'accessManagementUserGroupsCreateModeGroupTab';

// set default props
accessManagementUserGroupsCreateModeGroupTab.defaultProps = {};

// prop types
accessManagementUserGroupsCreateModeGroupTab.propTypes = {};
